import { Pencil, Plus } from 'lucide-react'
import { useId, useMemo, useState } from 'react'
import { Combobox } from '@/components/combobox'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { LibraryRule, RuleCondition } from '@/types/api'
import { useRule, useRuleOptions } from '../api'
import { OfflineBadge } from './offline-badge'
import { RuleDialog } from './rule-dialog'
import { RuleSentence } from './condition-builder'

export interface ConditionValue {
  rule_id: string | null
  unknown_as: boolean
}

/**
 * "Condition" section of campaign / schedule / channel-item forms (SIGNETIX_LOGIC.md §5):
 * play always, or only while a saved condition holds — pick one from the library or build a
 * new one; "When data is unavailable" → play anyway / don't play (`condition_unknown_as`).
 */
export function ConditionField({
  value,
  onChange,
  current,
  disabled,
  subject = 'it',
  className,
}: {
  value: ConditionValue
  onChange: (v: ConditionValue) => void
  /** The saved `condition` (name/edge) while the library list loads. */
  current?: RuleCondition | null
  disabled?: boolean
  /** "this schedule" — used in hints. */
  subject?: string
  className?: string
}) {
  const [conditional, setConditional] = useState(!!value.rule_id)
  const pickerId = useId()
  const list = useRuleOptions(conditional || !!value.rule_id)
  const selected = useRule(value.rule_id)
  const [dialog, setDialog] = useState<{ rule: LibraryRule | null } | null>(null)
  const rules = list.data ?? []
  const options = useMemo(() => {
    const out = rules.map((r) => ({ value: r.id, label: r.name, hint: r.edge ? undefined : 'Cloud only' }))
    if (value.rule_id && !out.some((o) => o.value === value.rule_id)) out.unshift({ value: value.rule_id, label: selected.data?.name ?? current?.name ?? 'Saved condition', hint: undefined })
    return out
  }, [rules, value.rule_id, selected.data?.name, current?.name])
  const rule = selected.data ?? rules.find((r) => r.id === value.rule_id) ?? null
  const edge = rule ? rule.edge : (current?.edge ?? true)

  return (
    <div className={cn('grid gap-3', className)}>
      <div role="radiogroup" aria-label="When it plays" className="grid gap-2 sm:grid-cols-2">
        {(
          [
            { on: false, label: 'Always', text: `No extra condition — ${subject} follows its schedule.` },
            { on: true, label: 'Only when…', text: 'A condition must hold on the screen (weather, location, data, time…).' },
          ] as const
        ).map((o) => (
          <button
            key={String(o.on)}
            type="button"
            role="radio"
            aria-checked={conditional === o.on}
            disabled={disabled}
            onClick={() => {
              setConditional(o.on)
              if (!o.on) onChange({ ...value, rule_id: null })
            }}
            className={cn('rounded-lg border p-2.5 text-left transition outline-none focus-visible:ring-[3px] focus-visible:ring-ring disabled:opacity-50', conditional === o.on ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'hover:bg-muted/50')}
          >
            <span className="block text-[13px] font-medium">{o.label}</span>
            <span className="block text-xs text-muted-foreground">{o.text}</span>
          </button>
        ))}
      </div>

      {conditional && (
        <div className="grid gap-2 rounded-lg border bg-muted/20 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-48 flex-1">
              <label htmlFor={pickerId} className="sr-only">
                Saved condition
              </label>
              <Combobox
                id={pickerId}
                value={value.rule_id}
                onChange={(id) => onChange({ ...value, rule_id: id })}
                options={options}
                disabled={disabled}
                placeholder={list.isPending ? 'Loading conditions…' : rules.length ? 'Choose a saved condition…' : 'No saved conditions yet'}
                searchPlaceholder="Search conditions…"
                emptyText="No conditions"
                aria-describedby={undefined}
              />
            </div>
            <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => setDialog({ rule: null })}>
              <Plus /> New condition
            </Button>
            {rule && (
              <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => setDialog({ rule })}>
                <Pencil /> Edit
              </Button>
            )}
          </div>
          {value.rule_id && (
            <div className="flex flex-wrap items-start gap-2 text-[12.5px]">
              <OfflineBadge edge={edge} />
              {rule ? <RuleSentence rule={rule.rule} className="min-w-0 flex-1" /> : <span className="text-muted-foreground">{current?.name}</span>}
            </div>
          )}
          {value.rule_id && !edge && <p className="text-[12px] text-destructive">Players can’t evaluate this condition offline — choose or build one that works offline.</p>}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[12.5px] text-muted-foreground">When the data is unavailable:</span>
            <div role="radiogroup" aria-label="When the data is unavailable" className="flex rounded-md border bg-muted/40 p-0.5">
              {(
                [
                  { v: true, label: 'Play anyway' },
                  { v: false, label: 'Don’t play' },
                ] as const
              ).map((o) => (
                <button
                  key={String(o.v)}
                  type="button"
                  role="radio"
                  aria-checked={value.unknown_as === o.v}
                  disabled={disabled}
                  onClick={() => onChange({ ...value, unknown_as: o.v })}
                  className={cn('rounded px-2 py-0.5 text-[12px] font-medium', value.unknown_as === o.v ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground')}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <RuleDialog
        open={!!dialog}
        rule={dialog?.rule ?? null}
        onOpenChange={(o) => !o && setDialog(null)}
        onSaved={(r) => onChange({ ...value, rule_id: r.id })}
      />
    </div>
  )
}

/** Compact condition chip for lists and tables. */
export function ConditionChip({ condition, className }: { condition: RuleCondition | null | undefined; className?: string }) {
  if (!condition) return null
  return (
    <span
      className={cn('inline-flex max-w-48 items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11.5px]', condition.edge ? 'border-primary/25 bg-primary/5' : 'border-warning/40 bg-warning/10', className)}
      title={`Only when “${condition.name}” holds${condition.unknown_as ? ' (plays when data is unavailable)' : ' (skipped when data is unavailable)'}${condition.edge ? '' : ' — needs a Cloud connection'}`}
    >
      <span className="text-muted-foreground">If</span>
      <span className="truncate font-medium">{condition.name}</span>
    </span>
  )
}
